import React from 'react';
import { Progress, Dropdown, Tooltip, Flex } from 'antd';
import { MoreOutlined, ArrowDownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { ECreditType } from '@leedpulse-monorepo/enums';
import { getNumberPlanBadgeClass, getSubscriptionStatusBadgeClass, SubscriptionStatusType, getPlanCreditsRemaining, getPlanCreditsDisplayState } from '../../helpers';
import { getActionMenuItems } from './helpers/subscriptionsPageHelpers';
import { ISubscriptionItem, ModalType } from './controllers/useSubscriptionsPageController';
import { IPlan } from '@leedpulse-monorepo/interfaces';
import { DegradedSubscription } from '../../contexts/auth/AuthenticationContext';

interface GetSubscriptionsColumnsParams {
  openModal: (type: ModalType, record?: ISubscriptionItem) => void;
  onUnassign: (record: ISubscriptionItem) => void;
  onCancelPlanChange: (record: ISubscriptionItem) => void;
  planMap?: Map<string, IPlan>;
  degradedSubscriptions: DegradedSubscription[];
  isCompanyExternallyBilled: boolean;
}

export const getSubscriptionsColumns = ({
  openModal,
  onUnassign,
  onCancelPlanChange,
  planMap,
  degradedSubscriptions,
  isCompanyExternallyBilled,
}: GetSubscriptionsColumnsParams): ColumnsType<ISubscriptionItem> => [
    {
      title: 'Numéro',
      dataIndex: 'sender_formatted',
      key: 'sender',
      render: (sender_formatted: string) => (
        <span className="numbers-page__phone">{sender_formatted}</span>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'id_plan',
      key: 'plan',
      render: (idPlan: ISubscriptionItem['id_plan'], record: ISubscriptionItem) =>
        idPlan ? (
          <Flex align="center" gap={6}>
            <span className={`numbers-page__plan-badge ${getNumberPlanBadgeClass(record.id_plan)}`}>{planMap?.get(record.id_plan)?.name ?? record.id_plan}</span>
            {record.pending_plan_change && (
              <Tooltip title={`Passage au plan ${planMap?.get(record.pending_plan_change.id_plan)?.name ?? record.pending_plan_change.id_plan} le ${dayjs(record.pending_plan_change.effective_at).format('DD/MM/YYYY')}`}>
                <ArrowDownOutlined className="numbers-page__plan-pending-icon" />
              </Tooltip>
            )}
          </Flex>
        ) : (
          <span className="numbers-page__plan-badge numbers-page__plan-badge--none">-</span>
        ),
    },
    {
      title: 'Utilisateur assigné',
      dataIndex: 'usernames',
      key: 'usernames',
      render: (usernames: string[]) =>
        usernames.length > 0 ? (
          <span className="numbers-page__user-name">{usernames.join(', ')}</span>
        ) : (
          <span className="numbers-page__user-empty">-</span>
        ),
    },
    {
      title: 'Forfait restant',
      dataIndex: 'credits_info',
      key: 'credits',
      render: (_, record) => {
        const info = record.credits_info;

        // No plan resolved yet → -
        if (!info) {
          return <span className="numbers-page__credits-na">-</span>;
        }

        if (info.isUnlimitedLeedbucks) {
          return <span className="numbers-page__credits-unlimited">∞</span>;
        }

        const total = info.total?.[ECreditType.leedbucks] || 0;
        const remaining = info.remaining?.[ECreditType.leedbucks] || 0;
        const planCredits = getPlanCreditsRemaining(total, remaining, false);

        return (
          <Flex align="center" gap={6} style={{ minWidth: 100 }}>
            <Progress
              percent={planCredits.remainingPercent}
              showInfo={false}
              strokeColor={planCredits.barColor}
              className="numbers-page__credits-bar"
              style={{ flex: 1, margin: 0 }}
            />
            <span className={`numbers-page__credits-percentage${planCredits.amber ? ' numbers-page__credits-percentage--amber' : ''}`}>{planCredits.remainingPercent}%</span>
          </Flex>
        );
      },
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: ISubscriptionItem['status'], record: ISubscriptionItem) => {
        const info = record.credits_info;
        const total = info?.total?.[ECreditType.leedbucks] || 0;
        const remaining = info?.remaining?.[ECreditType.leedbucks] || 0;
        const displayState = info && status === SubscriptionStatusType.ACTIF
          ? getPlanCreditsDisplayState(total, remaining, info.payg_enabled, info.isUnlimitedLeedbucks)
          : null;

        if (displayState === 'disabled') {
          return <span className="numbers-page__status-badge numbers-page__status-badge--overage-disabled">Hors forfait désactivé</span>;
        }
        if (displayState === 'overage') {
          return <span className="numbers-page__status-badge numbers-page__status-badge--overage">Hors forfait</span>;
        }
        if (displayState === 'exhausted') {
          return <span className="numbers-page__status-badge numbers-page__status-badge--exhausted">Épuisé</span>;
        }
        if (displayState === 'warning') {
          return <span className="numbers-page__status-badge numbers-page__status-badge--warning">À surveiller</span>;
        }
        return (
          <Tooltip title={status === SubscriptionStatusType.RESILIATION_EN_COURS ? `Actif jusqu'au ${new Date(record.plan_end).toLocaleDateString()}` : ''}>
            <span className={`numbers-page__status-badge ${getSubscriptionStatusBadgeClass(status)}`}>
              {status}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: getActionMenuItems({ record, onOpenModal: openModal, onUnassign, onCancelPlanChange, planMap, isDegradedMode: degradedSubscriptions.some((d) => d.id === record.id), isCompanyExternallyBilled }),
            onClick: ({ domEvent }) => domEvent.stopPropagation(),
          }}
          trigger={['click']}
          placement="bottomRight"
        >
          <button className="numbers-page__action-button" onClick={(e) => e.stopPropagation()}>
            <MoreOutlined style={{ fontSize: 18 }} />
          </button>
        </Dropdown>
      ),
    },
  ];
